import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Wallet,
  Shield,
  Globe,
  Euro,
  FileText,
  Users,
  PiggyBank,
  Zap,
  Check,
  Sparkles,
} from 'lucide-react';

const iconMap = {
  Wallet,
  Shield,
  Globe,
  Euro,
  FileText,
  Users,
  PiggyBank,
  Zap,
};

const BenefitsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="benefits" className="relative py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:80px_80px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4">
            {t.benefits.sectionTag}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {t.benefits.title}
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            {t.benefits.subtitle}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {t.benefits.items.map((benefit, index) => {
            const IconComponent = iconMap[benefit.icon];
            return (
              <div
                key={index}
                className="group relative p-6 rounded-2xl bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 hover:border-amber-500/30 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                  {IconComponent && (
                    <IconComponent className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-slate-400">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* UWA Feature Section */}
        <div className="relative rounded-3xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-blue-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 p-8 lg:p-12">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">
                  {t.benefits.uwa.tag}
                </span>
              </div>
              
              <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                {t.benefits.uwa.title}
              </h3>
              
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                {t.benefits.uwa.description}
              </p>

              {/* Feature List */}
              <ul className="space-y-4">
                {t.benefits.uwa.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Visual */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-md aspect-square">
                {/* Animated Rings */}
                <div className="absolute inset-0 rounded-full border border-amber-500/20 animate-ping" style={{ animationDuration: '3s' }} />
                <div className="absolute inset-4 rounded-full border border-amber-500/30 animate-ping" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }} />
                <div className="absolute inset-8 rounded-full border border-amber-500/40 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
                
                {/* Center Content */}
                <div className="absolute inset-16 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 backdrop-blur-sm border border-amber-500/30 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-amber-400 mb-2">UWA</div>
                    <div className="text-sm text-slate-400">Unternehmenswert-Anteil</div>
                  </div>
                </div>

                {/* Floating Elements */}
                {['24.99%', '50%', '€0'].map((value, i) => (
                  <div
                    key={i}
                    className="absolute px-4 py-2 rounded-full bg-slate-800/80 backdrop-blur-sm border border-slate-700/50 text-white text-sm font-medium animate-float"
                    style={{
                      top: i === 0 ? '10%' : i === 1 ? '70%' : '40%',
                      left: i === 0 ? '60%' : i === 1 ? '10%' : '75%',
                      animationDelay: `${i * 0.5}s`,
                    }}
                  >
                    {value}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
